import React, { useState, useEffect, useRef } from 'react';
import { Box, Card, Typography, TextField, Stack, Chip, IconButton, CircularProgress, Fab, Zoom } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';

const WebhookTable = ({ webhooks, onSearch, onOpenMenu, hasMore, isLoadingMore, loadMore, isLoading }) => {
  const [search, setSearch] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [showTopBtn, setShowTopBtn] = useState(false);

  const scrollContainerRef = useRef(null);
  const observerTarget = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (observerTarget.current) observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, isLoading, loadMore]);

  const handleScroll = (e) => {
    setShowTopBtn(e.target.scrollTop > 300);
  };

  const scrollToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Destination',
      flex: 1.8,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 1 }}>
          <Typography fontWeight={500} variant="body2">
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="gray">
            {params.row.description}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'url',
      headerName: 'URL',
      flex: 2,
      sortable: false,
      renderCell: (params) => {
        const url = params.value || '';

        const displayUrl = url.length > 35 ? `${url.substring(0, 16)}...${url.substring(url.length - 12)}` : url;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LanguageIcon fontSize="small" sx={{ color: '#93c5fd', flexShrink: 0 }} />
              <Typography
                variant="body2"
                color="#93c5fd"
                title={url}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: '0.8125rem',
                  cursor: 'default',
                }}
              >
                {displayUrl}
              </Typography>
            </Stack>
          </Box>
        );
      },
    },
    {
      field: 'event_types',
      headerName: 'Events',
      flex: 3.5,
      sortable: false,
      renderCell: (params) => {
        const w = params.row;
        const isExpanded = expandedRow === w.id;
        const visibleEvents = isExpanded ? w.event_types : w.event_types.slice(0, 4);
        const remaining = w.event_types.length - 4;

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 2, alignItems: 'center', minHeight: '100%' }}>
            {visibleEvents.map((e) => (
              <Chip
                key={e}
                label={e}
                size="small"
                sx={{ backgroundColor: 'grey.900', color: '#e5e7eb', border: '1px solid #374151' }}
              />
            ))}
            {!isExpanded && remaining > 0 && (
              <Chip
                label={`+${remaining} more`}
                size="small"
                onClick={() => setExpandedRow(w.id)}
                sx={{ backgroundColor: 'grey.900', color: '#93c5fd', cursor: 'pointer' }}
              />
            )}
            {isExpanded && (
              <Chip
                label="View less"
                size="small"
                onClick={() => setExpandedRow(null)}
                sx={{ backgroundColor: 'grey.800', color: '#93c5fd', cursor: 'pointer' }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'active',
      headerName: 'Status',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            {params.value ? (
              <>
                <CheckCircleIcon sx={{ color: '#22c55e', fontSize: 18 }} />
                <Typography variant="body2" color="#22c55e">
                  Active
                </Typography>
              </>
            ) : (
              <>
                <CancelIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                <Typography variant="body2" color="#94a3b8">
                  Inactive
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <IconButton size="small" onClick={(e) => onOpenMenu(e, params.row)}>
            <MoreHorizIcon sx={{ color: '#ffffff' }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Card
      sx={{
        background: 'grey.900',
        borderRadius: 2,
        p: 3,
        height: '60vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Typography fontWeight={600} fontSize={22} sx={{ color: '#fff' }}>
          Configured Webhooks
        </Typography>
        <TextField
          placeholder="Search by name..."
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch(search.trim())}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} /> }}
          sx={{
            width: 300,
            '& .MuiInputBase-root': { color: '#fff', borderRadius: 2 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
          }}
        />
      </Box>

      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowY: 'auto',
          position: 'relative',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: '#374151', borderRadius: '4px' },
        }}
      >
        <DataGrid
          rows={webhooks}
          columns={columns}
          getRowId={(row) => row.id}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          loading={isLoading}
          hideFooter
          autoHeight
          disableVirtualization
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': { borderBottom: '1px solid #374151' },
            '& .MuiDataGrid-columnHeaderTitle': { color: 'gray', fontWeight: 600 },
            '& .MuiDataGrid-row': {
              borderBottom: '1px solid #374151',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04) !important' },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              display: 'flex',
              alignItems: 'center',
              py: 1,
            },
          }}
        />
        <div ref={observerTarget} style={{ height: '10px', width: '100%' }} />

        {isLoadingMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, backgroundColor: 'grey.900' }}>
            <CircularProgress size={24} sx={{ color: '#93c5fd' }} />
            <Typography variant="caption" sx={{ ml: 2, color: '#93c5fd', alignSelf: 'center' }}>
              Loading more...
            </Typography>
          </Box>
        )}
      </Box>

      <Zoom in={showTopBtn}>
        <Fab
          size="small"
          onClick={scrollToTop}
          sx={{
            position: 'absolute',
            bottom: 24,
            right: 24,
            backgroundColor: '#3b82f6',
            '&:hover': { backgroundColor: '#2563eb' },
          }}
        >
          <ArrowUpwardOutlinedIcon sx={{ color: '#fff' }} />
        </Fab>
      </Zoom>
    </Card>
  );
};

export default WebhookTable;
